import {Component, DestroyRef, inject, Input, OnDestroy, OnInit} from '@angular/core';
import {
  AangifteBerekening,
  BokkzIbDataService,
  IbRekenService,
  IncomeTaxEntity
} from '@snelstart-ventures/fiscaal/domain-logic';
import {FormControl, FormGroup} from '@angular/forms';
import {BehaviorSubject, debounceTime, filter, map, Observable, shareReplay, Subscription, switchMap, take} from 'rxjs';
import {SharedUiFormControlsModule} from '@snelstart/ui-form-controls';
import {SharedUiDetailsModule} from '@snelstart/ui-details';
import {SharedUiButtonModule} from '@snelstart/ui-button';
import {SharedUiAlertModule} from '@snelstart/ui-alert';
import {CommonModule} from '@angular/common';
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {SharedUiLoadingModule} from "@snelstart/ui-loading";

@Component({
  selector: 'snelstart-venture-fiscaal-feature-verdeling',
  templateUrl: './verdeling.component.html',
  styleUrls: ['./verdeling.component.scss'],
  imports: [
    SharedUiFormControlsModule,
    SharedUiDetailsModule,
    SharedUiButtonModule,
    SharedUiAlertModule,
    CommonModule,
    SharedUiLoadingModule
  ]
})
export class VerdelingComponent implements OnInit, OnDestroy {

  @Input() public id!: string;
  private dataService = inject(BokkzIbDataService);
  private bokkzClient = inject(IbRekenService);
  private destroyRef = inject(DestroyRef);
  private changeSubscriptions = new Subscription();
  public heeftVerdeling = false;
  public minValueEw = 0;
  public maxValueEw = 0;
  public eersteVerdeelpost = 0;
  public dataVisible$ = new BehaviorSubject<boolean>(false);

  public formGroup = new FormGroup({
    gekozenAangever: new FormGroup({
      aandeelEigenWoning: new FormControl<number | null>(null),
      aandeelBox2: new FormControl<number | null>(null),
      aandeelBox3: new FormControl<number | null>(null),
      aandeelRendementsGrondslag: new FormControl<number | null>(null),
      aandeelPGA: new FormControl<number | null>(null),
      aandeelDividendbelasting: new FormControl<number | null>(null),
      verschuldigdeBelasting: new FormControl<number | null>(null),
    }),
    gekozenPartner: new FormGroup({
      aandeelEigenWoning: new FormControl<number | null>(null),
      aandeelBox2: new FormControl<number | null>(null),
      aandeelBox3: new FormControl<number | null>(null),
      aandeelRendementsGrondslag: new FormControl<number | null>(null),
      aandeelPGA: new FormControl<number | null>(null),
      aandeelDividendbelasting: new FormControl<number | null>(null),
      verschuldigdeBelasting: new FormControl<number | null>(null),
    }),
    totaalTeVerdelen: new FormGroup({
      totaalEigenWoning: new FormControl<number | null>(null),
      totaalBox2: new FormControl<number | null>(null),
      totaalBox3: new FormControl<number | null>(null),
      totaalRendementsGrondslag: new FormControl<number | null>(null),
      totaalPGA: new FormControl<number | null>(null),
      totaalDividendbelasting: new FormControl<number | null>(null),
    }),
    optimaalAangever: new FormGroup({
      aandeelEigenWoning: new FormControl<number | null>(null),
      aandeelBox2: new FormControl<number | null>(null),
      aandeelBox3: new FormControl<number | null>(null),
      aandeelRendementsGrondslag: new FormControl<number | null>(null),
      aandeelPGA: new FormControl<number | null>(null),
      aandeelDividendbelasting: new FormControl<number | null>(null),
      verschuldigdeBelasting: new FormControl<number | null>(null),
    }),
    optimaalPartner: new FormGroup({
      aandeelEigenWoning: new FormControl<number | null>(null),
      aandeelBox2: new FormControl<number | null>(null),
      aandeelBox3: new FormControl<number | null>(null),
      aandeelRendementsGrondslag: new FormControl<number | null>(null),
      aandeelPGA: new FormControl<number | null>(null),
      aandeelDividendbelasting: new FormControl<number | null>(null),
      verschuldigdeBelasting: new FormControl<number | null>(null),
    }),
    totaalVerschuldigdeBelastingGekozen: new FormControl<number | null>(null),
    totaalVerschuldigdeBelastingOptimaal: new FormControl<number | null>(null),
  });

  readonly incomeTaxEntity$: Observable<IncomeTaxEntity> =
    this.dataService.incomeTaxEntity$.pipe(
      filter((data): data is IncomeTaxEntity => !!data?.sstRelationId),
      takeUntilDestroyed(this.destroyRef),
      shareReplay({bufferSize: 1, refCount: true})
    );

  ngOnInit(): void {
    this.incomeTaxEntity$.pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(entity => {
      this.heeftVerdeling = this.hasVerdeelposten(entity)
      this.eersteVerdeelpost = this.getEersteVerdeelpost(entity);
      this.formGroup.reset({}, {emitEvent: false});
      if (this.heeftVerdeling) {
        this.bokkzClient.getIncomeTaxCalculation(entity.aangifte!).pipe(
          take(1),
          switchMap((berekening) =>
            this.bokkzClient.getIncomeTaxOptimalization(entity.aangifte!).pipe(
              take(1),
              switchMap((optimalisatie) =>
                this.bokkzClient.getIncomeTaxCalculation(optimalisatie).pipe(
                  take(1),
                  map((ibBerekeningOptimalisatie) => ({
                    berekening,
                    optimalisatie,
                    ibBerekeningOptimalisatie,
                  }))
                )
              )
            )
          ),
          takeUntilDestroyed(this.destroyRef)
        ).subscribe(({berekening, optimalisatie, ibBerekeningOptimalisatie}) => {
          const saldo = berekening.box1?.opbouwInkomen?.saldoEigenWoning ?? 0;
          this.minValueEw = Math.min(0, saldo);
          this.maxValueEw = Math.max(0, saldo);
          this.eersteVerdeelpost = this.getEersteVerdeelpost(berekening);

          this.formGroup.patchValue(
              {
                  gekozenAangever: {
                      aandeelEigenWoning:
                          entity.aangifte?.verdeling?.aandeelEigenWoning ?? berekening.box1?.opbouwInkomen?.inkomstenUitEigenWoning ?? 0,
                      aandeelBox2:
                          entity.aangifte?.verdeling?.aandeelBox2 ?? berekening.box2?.opbouwInkomen?.inkomenUitAanmerkelijkBelang ?? 0,
                      aandeelBox3: entity.aangifte?.verdeling?.aandeelBox3 ?? berekening.box3?.opbouwInkomen?.grondslagNaVerdeling ?? 0,
                      aandeelRendementsGrondslag:
                          entity.aangifte?.verdeling?.aandeelRendementsGrondslag ??
                          berekening.box3?.opbouwInkomen?.rendementsGrondslagNaVerdeling ??
                          0,
                      aandeelPGA:
                          entity.aangifte?.verdeling?.aandeelPGA ??
                          (berekening.box1?.persoonsgebondenAftrek ?? 0) +
                              (berekening.box2?.persoonsgebondenAftrek ?? 0) +
                              (berekening.box3?.persoonsgebondenAftrek ?? 0),
                      aandeelDividendbelasting:
                          entity.aangifte?.verdeling?.aandeelDividendbelasting ?? berekening.alBetaaldeDividendbelasting ?? 0,
                      verschuldigdeBelasting: berekening.verschuldigdeInkomstenbelasting ?? 0,
                  },
                  gekozenPartner: {
                      verschuldigdeBelasting: berekening.partnerBerekening?.verschuldigdeInkomstenbelasting ?? 0,
                  },
                  optimaalAangever: {
                      aandeelEigenWoning: optimalisatie.verdeling?.aandeelEigenWoning ?? 0,
                      aandeelBox2: optimalisatie.verdeling?.aandeelBox2 ?? 0,
                      aandeelBox3: optimalisatie.verdeling?.aandeelBox3 ?? 0,
                      aandeelRendementsGrondslag: optimalisatie.verdeling?.aandeelRendementsGrondslag ?? 0,
                      aandeelPGA: optimalisatie.verdeling?.aandeelPGA ?? 0,
                      aandeelDividendbelasting: optimalisatie.verdeling?.aandeelDividendbelasting ?? 0,
                      verschuldigdeBelasting: ibBerekeningOptimalisatie.verschuldigdeInkomstenbelasting ?? 0,
                  },
                  totaalTeVerdelen: {
                      totaalEigenWoning: berekening.box1?.opbouwInkomen?.saldoEigenWoning ?? 0,
                      totaalBox2: berekening.box2?.opbouwInkomen?.saldoAanmerkelijkBelang ?? 0,
                      totaalBox3: berekening.box3?.opbouwInkomen?.grondslagSparenEnBeleggen ?? 0,
                      totaalRendementsGrondslag: berekening.box3?.opbouwInkomen?.rendementsGrondslag ?? 0,
                      totaalPGA: (optimalisatie.verdeling?.aandeelPGA ?? 0) + (optimalisatie.partnerAangifte?.verdeling?.aandeelPGA ?? 0),
                      totaalDividendbelasting:
                          (optimalisatie.verdeling?.aandeelDividendbelasting ?? 0) +
                          (optimalisatie.partnerAangifte?.verdeling?.aandeelDividendbelasting ?? 0),
                  },
                  optimaalPartner: {
                      verschuldigdeBelasting: ibBerekeningOptimalisatie.partnerBerekening?.verschuldigdeInkomstenbelasting ?? 0,
                  },
              },
              { emitEvent: false },
          );
          this.getOptimaalPartnerDeel();
          this.recalculateTotals();
          this.dataVisible$.next(true);
        });
      } else {
        this.dataVisible$.next(true);
      }
    });
    this.subscribeToChanges();
  }

  ngOnDestroy(): void {
    this.changeSubscriptions.unsubscribe();
    this.saveForm();
  }

  private subscribeToChanges(): void {
    this.changeSubscriptions.unsubscribe();
    this.changeSubscriptions = new Subscription();

    this.changeSubscriptions.add(
      this.formGroup.controls.gekozenAangever.valueChanges.pipe(
        debounceTime(700),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe(() => this.recalculateTotals())
    );
  }

  private recalculateTotals(): void {
    this.saveForm();

    this.bokkzClient.getIncomeTaxCalculation(this.dataService.incomeTaxEntity.value.aangifte!).pipe(
      take(1),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((data) => {
      this.formGroup.patchValue({
        gekozenAangever: {
          verschuldigdeBelasting: data.verschuldigdeInkomstenbelasting ?? 0,
        },
        gekozenPartner: {
          verschuldigdeBelasting: data.partnerBerekening?.verschuldigdeInkomstenbelasting ?? 0,
        },
      }, {emitEvent: false});
    });
  }

  hasVerdeelposten(data: IncomeTaxEntity): boolean {
    const hasEigenWoningen = (data.aangifte?.box1?.belastbareInkomstenUitEigenWoning?.eigenWoningen?.length ?? 0) > 0;
    const hasAanmerkelijkBelang = (data.aangifte?.box2?.aanmerkelijkBelang?.length ?? 0) > 0;
    const hasBox3 = (data.aangifte?.box3 !== undefined &&
        (data.aangifte?.box3.bankrekeningen != undefined || data.aangifte?.box3.beleggingsrekeningen != undefined ||
         data.aangifte?.box3.overigeOnroerendeZaken != undefined || data.aangifte?.box3.overigeBezittingen != undefined));
    const hasPersoonsgebondenAftrek = data.aangifte?.persoonsgebondenAftrek !== undefined;
    const hasIngehoudenDividendbelasting = (data.aangifte?.voorheffingen?.ingehoudenDividendbelasting?.length ?? 0) > 0;

    console.log('hasEigenWoningen:', hasEigenWoningen);
    console.log('hasAanmerkelijkBelang:', hasAanmerkelijkBelang);
    console.log('hasBox3:', hasBox3);
    console.log('hasPersoonsgebondenAftrek:', hasPersoonsgebondenAftrek);
    console.log('hasIngehoudenDividendbelasting:', hasIngehoudenDividendbelasting);

    return hasEigenWoningen
      || hasAanmerkelijkBelang
      || hasBox3
      || hasPersoonsgebondenAftrek
      || hasIngehoudenDividendbelasting;
  }

  getEersteVerdeelpost(data: AangifteBerekening): number {
    const hasEigenWoningen = data.box1?.opbouwInkomen?.saldoEigenWoning ?? 0 !== 0;
    const hasAanmerkelijkBelang = data.box2?.opbouwInkomen?.saldoAanmerkelijkBelang ?? 0 > 0;
    const hasBox3 = data.box3?.opbouwInkomen?.rendementsGrondslag ?? 0 !== 0;
    const hasPersoonsgebondenAftrek = data.box1?.persoonsgebondenAftrek ?? 0 !== 0;

    if (hasEigenWoningen) {
      return 1;
    }
    if(hasAanmerkelijkBelang) {
      return 2;
    }
    if(hasBox3) {
      return 3;
    }
    if(hasPersoonsgebondenAftrek) {
      return 4;
    }
    else {
      return 5;
    }
  }

  getVerschuldigdeBelasting(): any {
    const aangever = this.formGroup.controls.gekozenAangever.getRawValue();
    const partner = this.formGroup.controls.gekozenPartner.getRawValue();
    const sum = (aangever.verschuldigdeBelasting ?? 0) + (partner.verschuldigdeBelasting ?? 0);
    this.formGroup.patchValue({totaalVerschuldigdeBelastingGekozen: sum,}, {emitEvent: false});
    return sum;
  }

  getVerschuldigdeBelastingOptimaal(): any {
    const aangever = this.formGroup.controls.optimaalAangever.getRawValue();
    const partner = this.formGroup.controls.optimaalPartner.getRawValue();
    const sum = (aangever.verschuldigdeBelasting ?? 0) + (partner.verschuldigdeBelasting ?? 0);
    this.formGroup.patchValue({totaalVerschuldigdeBelastingOptimaal: sum,}, {emitEvent: false});
    return sum;
  }

  getOptimaalPartnerDeel() {
    const totaal = this.formGroup.controls.totaalTeVerdelen.getRawValue();
    const gekozen = this.formGroup.controls.gekozenAangever.getRawValue();
    const optimaalAangever = this.formGroup.controls.optimaalAangever.getRawValue();

    this.formGroup.patchValue({
      gekozenPartner: {
        aandeelEigenWoning: (totaal.totaalEigenWoning ?? 0) - (gekozen.aandeelEigenWoning ?? 0),
        aandeelBox2: (totaal.totaalBox2 ?? 0) - (gekozen.aandeelBox2 ?? 0),
        aandeelBox3: (totaal.totaalBox3 ?? 0) - (gekozen.aandeelBox3 ?? 0),
        aandeelRendementsGrondslag: (totaal.totaalRendementsGrondslag ?? 0) - (gekozen.aandeelRendementsGrondslag ?? 0),
        aandeelPGA: (totaal.totaalPGA ?? 0) - (gekozen.aandeelPGA ?? 0),
        aandeelDividendbelasting: (totaal.totaalDividendbelasting ?? 0) - (gekozen.aandeelDividendbelasting ?? 0),
      },
      optimaalPartner: {
        aandeelEigenWoning: (totaal.totaalEigenWoning ?? 0) - (optimaalAangever.aandeelEigenWoning ?? 0),
        aandeelBox2: (totaal.totaalBox2 ?? 0) - (optimaalAangever.aandeelBox2 ?? 0),
        aandeelBox3: (totaal.totaalBox3 ?? 0) - (optimaalAangever.aandeelBox3 ?? 0),
        aandeelRendementsGrondslag: (totaal.totaalRendementsGrondslag ?? 0) - (optimaalAangever.aandeelRendementsGrondslag ?? 0),
        aandeelPGA: (totaal.totaalPGA ?? 0) - (optimaalAangever.aandeelPGA ?? 0),
        aandeelDividendbelasting: (totaal.totaalDividendbelasting ?? 0) - (optimaalAangever.aandeelDividendbelasting ?? 0),
      },
    }, {emitEvent: false});
  }

  getPartnerDeelEw(): any {
    const totaal = this.formGroup.controls.totaalTeVerdelen.getRawValue();
    const gekozen = this.formGroup.controls.gekozenAangever.getRawValue();
    const sum = (totaal.totaalEigenWoning ?? 0) - (gekozen.aandeelEigenWoning ?? 0);

    this.formGroup.patchValue({
      gekozenPartner: {
        aandeelEigenWoning: sum,
      },
    }, {emitEvent: false});
    return sum;
  }

  getPartnerDeelBox2(): any {
    const totaal = this.formGroup.controls.totaalTeVerdelen.getRawValue();
    const gekozen = this.formGroup.controls.gekozenAangever.getRawValue();
    const sum = (totaal.totaalBox2 ?? 0) - (gekozen.aandeelBox2 ?? 0);

    this.formGroup.patchValue({
      gekozenPartner: {
        aandeelBox2: sum,
      },
    }, {emitEvent: false});
    return sum;
  }

  getPartnerDeelBox3(): any {
    const totaal = this.formGroup.controls.totaalTeVerdelen.getRawValue();
    const gekozen = this.formGroup.controls.gekozenAangever.getRawValue();
    const sum = (totaal.totaalBox3 ?? 0) - (gekozen.aandeelBox3 ?? 0);

    const totaal1 = this.formGroup.controls.totaalTeVerdelen.getRawValue();
    const gekozen1 = this.formGroup.controls.gekozenAangever.getRawValue();
    const sum1 = (totaal1.totaalRendementsGrondslag ?? 0) - (gekozen1.aandeelRendementsGrondslag ?? 0);

    this.formGroup.patchValue({
      gekozenPartner: {
        aandeelBox3: sum,
        aandeelRendementsGrondslag: sum1,
      },
    }, {emitEvent: false});
    return sum;
  }

  getPartnerDeelPga(): any {
    const totaal = this.formGroup.controls.totaalTeVerdelen.getRawValue();
    const gekozen = this.formGroup.controls.gekozenAangever.getRawValue();
    const sum = (totaal.totaalPGA ?? 0) - (gekozen.aandeelPGA ?? 0);

    this.formGroup.patchValue({
      gekozenPartner: {
        aandeelPGA: sum,
      },
    }, {emitEvent: false});
    return sum;
  }

  getPartnerDeelDiv(): any {
    const totaal = this.formGroup.controls.totaalTeVerdelen.getRawValue();
    const gekozen = this.formGroup.controls.gekozenAangever.getRawValue();
    const sum = (totaal.totaalDividendbelasting ?? 0) - (gekozen.aandeelDividendbelasting ?? 0);

    this.formGroup.patchValue({
      gekozenPartner: {
        aandeelDividendbelasting: sum,
      },
    }, {emitEvent: false});
    return sum;
  }

  public saveForm() {
    const verdeling = this.formGroup.getRawValue();

    this.dataService.patchIncomeTaxEntity(currentEntity => {
      const aangifte: any = {
        ...currentEntity.aangifte,
        verdeling: {
          ...currentEntity.aangifte?.verdeling,
          aandeelEigenWoning: verdeling.gekozenAangever.aandeelEigenWoning ?? undefined,
          aandeelBox2: verdeling.gekozenAangever.aandeelBox2 ?? undefined,
          aandeelBox3: verdeling.gekozenAangever.aandeelBox3 ?? undefined,
          aandeelRendementsGrondslag: verdeling.gekozenAangever.aandeelRendementsGrondslag ?? undefined,
          aandeelPGA: verdeling.gekozenAangever.aandeelPGA ?? undefined,
          aandeelDividendbelasting: verdeling.gekozenAangever.aandeelDividendbelasting ?? undefined,
        },
        partnerAangifte: {
          ...currentEntity.aangifte?.partnerAangifte,
          verdeling: {
            ...currentEntity.aangifte?.verdeling,
            aandeelEigenWoning: verdeling.gekozenPartner.aandeelEigenWoning ?? undefined,
            aandeelBox2: verdeling.gekozenPartner.aandeelBox2 ?? undefined,
            aandeelBox3: verdeling.gekozenPartner.aandeelBox3 ?? undefined,
            aandeelRendementsGrondslag: verdeling.gekozenPartner.aandeelRendementsGrondslag ?? undefined,
            aandeelPGA: verdeling.gekozenPartner.aandeelPGA ?? undefined,
            aandeelDividendbelasting: verdeling.gekozenPartner.aandeelDividendbelasting ?? undefined,
          }
        }
      };
      return {
        ...currentEntity,
        aangifte
      };
    });
  }

}
